<template>
  <div class="card">
    <div class="card-header">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item"><a href="javascript:void(0)">LXD</a></li>
          <li class="breadcrumb-item"><a href="javascript:void(0)">List Instances</a></li>
        </ol>
      </nav>
    </div>
    <div class="card-body mt-0">
      <div class="row">
        <div class="col-md-12 d-flex justify-content-center align-items-center mb-3">
          <h4 class="fw-600 mb-0 mt-0">LXD Management</h4>
        </div>

        <div class="col-md-12 mb-3">
          <div class="lxd-controls-row">
            <div class="lxd-cluster-wrap">
              <label class="lxd-label">Cluster</label>
              <multi-select
                v-model="selectedCluster"
                :options="allClusters"
                label="name"
                track-by="name"
                placeholder="Select a cluster"
              />
            </div>
            <div class="lxd-controls-right">
              <div class="lxd-search-wrap">
                <i class="fa fa-search lxd-search-icon"></i>
                <input
                  v-model="search"
                  type="text"
                  class="lxd-search"
                  placeholder="Search name, IP, node…"
                />
                <button v-if="search" class="lxd-search-clear" @click="search = ''">
                  <i class="fa fa-times"></i>
                </button>
              </div>
              <select v-model="pageSize" class="lxd-select">
                <option :value="25">25 / page</option>
                <option :value="50">50 / page</option>
                <option :value="100">100 / page</option>
              </select>
            </div>
          </div>
        </div>

        <div class="col-md-12">
          <!-- Toolbar -->


          <!-- Table -->
          <div class="lxd-table-wrap">
            <table class="lxd-table">
              <thead>
                <tr>
                  <th @click="setSort('name')" class="sortable" style="width:22%">
                    Name <i class="fa" :class="sortIcon('name')"></i>
                  </th>
                  <th @click="setSort('status')" class="sortable text-center" style="width:9%">
                    Status <i class="fa" :class="sortIcon('status')"></i>
                  </th>
                  <th @click="setSort('ip_address')" class="sortable text-center" style="width:14%">
                    IP Address <i class="fa" :class="sortIcon('ip_address')"></i>
                  </th>
                  <th @click="setSort('node')" class="sortable text-center" style="width:10%">
                    Node <i class="fa" :class="sortIcon('node')"></i>
                  </th>
                  <th @click="setSort('profiles')" class="sortable text-center" style="width:15%">
                    Profile <i class="fa" :class="sortIcon('profiles')"></i>
                  </th>
                  <th @click="setSort('type')" class="sortable text-center" style="width:8%">
                    Type <i class="fa" :class="sortIcon('type')"></i>
                  </th>
                  <th @click="setSort('created')" class="sortable text-center" style="width:12%">
                    Created <i class="fa" :class="sortIcon('created')"></i>
                  </th>
                </tr>
              </thead>
              <tbody v-if="loader">
                <tr v-for="i in 8" :key="i">
                  <td colspan="7" class="p-0">
                    <div class="lxd-skeleton"></div>
                  </td>
                </tr>
              </tbody>
              <tbody v-else-if="pagedInstances.length === 0">
                <tr>
                  <td colspan="7" class="lxd-empty">
                    <i class="fa fa-server fa-2x mb-2 d-block text-muted"></i>
                    {{ search ? 'No instances match your search.' : 'No instances found.' }}
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr
                  v-for="(row, index) in pagedInstances"
                  :key="index"
                  class="lxd-row"
                >
                  <td @click="openDetails(row)" style="cursor:pointer">
                    <div class="lxd-copy-cell">
                      <span class="lxd-name">{{ row.name }}</span>
                      <button class="lxd-copy-btn" title="Copy name" @click.stop.prevent="copyText(row.name)">
                        <i class="fa fa-copy"></i>
                      </button>
                    </div>
                  </td>
                  <td class="text-center">
                    <span :class="statusClass(row.status)" class="lxd-badge">
                      {{ row.status }}
                    </span>
                  </td>
                  <td class="text-center lxd-mono">
                    <div class="lxd-copy-cell lxd-copy-cell--center">
                      <span>{{ row.ip_address || '—' }}</span>
                      <button v-if="row.ip_address" class="lxd-copy-btn" title="Copy IP" @click.stop.prevent="copyText(row.ip_address)">
                        <i class="fa fa-copy"></i>
                      </button>
                    </div>
                  </td>
                  <td class="text-center">
                    <span class="lxd-node">{{ row.node || '—' }}</span>
                  </td>
                  <td class="text-center">
                    <span v-if="row.profiles && row.profiles.length" class="lxd-profiles">
                      <span v-for="p in row.profiles" :key="p" class="lxd-profile-tag">{{ p }}</span>
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-center">
                    <span v-if="row.type" class="lxd-type">{{ row.type }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-center lxd-date">{{ formatDate(row.created) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="lxd-pagination">
            <div class="lxd-pagination__info">
              Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ filteredInstances.length }}
            </div>
            <div class="lxd-pagination__controls">
              <button class="lxd-page-btn" :disabled="currentPage === 1" @click="currentPage = 1">
                <i class="fa fa-angle-double-left"></i>
              </button>
              <button class="lxd-page-btn" :disabled="currentPage === 1" @click="currentPage--">
                <i class="fa fa-angle-left"></i>
              </button>
              <button
                v-for="p in visiblePages"
                :key="p"
                class="lxd-page-btn"
                :class="{ 'lxd-page-btn--active': p === currentPage }"
                @click="currentPage = p"
              >{{ p }}</button>
              <button class="lxd-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">
                <i class="fa fa-angle-right"></i>
              </button>
              <button class="lxd-page-btn" :disabled="currentPage === totalPages" @click="currentPage = totalPages">
                <i class="fa fa-angle-double-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import MultiSelect from 'vue-multiselect'
import copyButtonMixin from '@/mixins/copyButtonMixin'

export default {
  name: 'LxdIndex',
  components: { MultiSelect },
  mixins: [copyButtonMixin],
  data() {
    return {
      instances: [],
      loader: false,
      refreshing: false,
      lastUpdated: null,
      search: '',
      sortKey: 'created',
      sortDir: 'desc',
      currentPage: 1,
      pageSize: 25,
      allClusters: [
        { name: 'LX4 (Testing)', value: 'lx4' },
        { name: 'ORD (Production)', value: 'ord' },
        { name: 'EGV (Production)', value: 'egv' },
        { name: 'IAD (Production)', value: 'iad' },
        { name: 'OVH (Production)', value: 'ovh' },
        { name: 'Showit (Production)', value: 'showit' },
      ],
      selectedCluster: {},
    }
  },
  computed: {
    filteredInstances() {
      const q = this.search.toLowerCase().trim()
      let list = this.instances
      if (q) {
        list = list.filter(r =>
          (r.name || '').toLowerCase().includes(q) ||
          (r.ip_address || '').toLowerCase().includes(q) ||
          (r.node || '').toLowerCase().includes(q) ||
          (r.status || '').toLowerCase().includes(q)
        )
      }
      const key = this.sortKey
      const dir = this.sortDir === 'asc' ? 1 : -1
      return [...list].sort((a, b) => {
        const av = (a[key] || '').toString().toLowerCase()
        const bv = (b[key] || '').toString().toLowerCase()
        return av < bv ? -dir : av > bv ? dir : 0
      })
    },
    totalPages() {
      return Math.ceil(this.filteredInstances.length / this.pageSize)
    },
    pageStart() {
      return (this.currentPage - 1) * this.pageSize
    },
    pageEnd() {
      return Math.min(this.pageStart + this.pageSize, this.filteredInstances.length)
    },
    pagedInstances() {
      return this.filteredInstances.slice(this.pageStart, this.pageEnd)
    },
    visiblePages() {
      const total = this.totalPages
      const cur = this.currentPage
      const delta = 2
      const pages = []
      for (let i = Math.max(1, cur - delta); i <= Math.min(total, cur + delta); i++) {
        pages.push(i)
      }
      return pages
    },
  },
  watch: {
    search() { this.currentPage = 1 },
    pageSize() { this.currentPage = 1 },
    selectedCluster(val) {
      if (val && val.value) this.fetchClusterInstances()
    },
  },
  mounted() {
    const cluster = localStorage.getItem('cluster')
    if (cluster) {
      this.selectedCluster = this.allClusters.find(c => c.value === cluster) || {}
    }
    localStorage.removeItem('cluster')
    localStorage.removeItem('instance')
    this.fetchClusterInstances()
  },
  methods: {
    copyText(text) {
      this.doCopy(text)
    },
    openDetails(data) {
      localStorage.setItem('cluster', this.selectedCluster.value)
      localStorage.setItem('instance', data.name)
      this.$router.push({ name: 'lxd-details', params: { id: data.name } })
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDir = 'asc'
      }
      this.currentPage = 1
    },
    sortIcon(key) {
      if (this.sortKey !== key) return 'fa-sort text-muted'
      return this.sortDir === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc'
    },
    statusClass(status) {
      const s = (status || '').toLowerCase()
      if (s === 'running') return 'lxd-badge--running'
      if (s === 'stopped') return 'lxd-badge--stopped'
      return 'lxd-badge--other'
    },
    formatDate(date) {
      return moment(date, 'YYYY-MM-DD').format('D MMM YYYY')
    },
    async fetchClusterInstances() {
      this.currentPage = 1
      const cluster = this.selectedCluster.value || 'lx4'
      const cacheKey = `lxd_cache_${cluster}`

      const cached = localStorage.getItem(cacheKey)
      if (cached) {
        try {
          const { data, updatedAt } = JSON.parse(cached)
          this.instances = data
          this.lastUpdated = moment(updatedAt).format('HH:mm:ss')
          this.refreshing = true
        } catch (e) { /* ignore */ }
      } else {
        this.loader = true
      }

      try {
        const resp = await this.$http.post('/lxd/list', { grant_type: 'token', cluster })
        this.instances = resp.data.data.instances
        const now = new Date().toISOString()
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ data: this.instances, updatedAt: now }))
        } catch (e) { /* quota exceeded */ }
        this.lastUpdated = moment(now).format('HH:mm:ss')
      } catch (e) {
        console.error('fetchClusterInstances', e)
      } finally {
        this.loader = false
        this.refreshing = false
      }
    },
  },
}
</script>

<style scoped>
.card {
  margin: 20px;
}

/* Controls row */
.lxd-controls-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
}
.lxd-cluster-wrap {
  flex: 0 0 300px;
}
.lxd-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #495057;
}
.lxd-controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  justify-content: flex-end;
}
.lxd-status-text {
  font-size: 12px;
  color: #6c757d;
}
.lxd-count-badge {
  font-size: 12px;
  background: #e9ecef;
  color: #495057;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 600;
}

/* Search */
.lxd-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.lxd-search-icon {
  position: absolute;
  left: 10px;
  color: #adb5bd;
  font-size: 13px;
  pointer-events: none;
}
.lxd-search {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 32px 6px 30px;
  font-size: 13px;
  width: 220px;
  outline: none;
  transition: border-color 0.15s;
}
.lxd-search:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 2px rgba(0,123,255,0.15);
}
.lxd-search-clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  line-height: 1;
}
.lxd-search-clear:hover { color: #495057; }

.lxd-select {
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  outline: none;
  cursor: pointer;
}

/* Table */
.lxd-table-wrap {
  overflow-x: auto;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}
.lxd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  table-layout: fixed;
}
.lxd-table thead {
  background: lightgrey;
  color: black;
}
.lxd-table thead th {
  padding: 11px 14px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  border: none;
  user-select: none;
}
.lxd-table thead th.sortable {
  cursor: pointer;
}
.lxd-table thead th.sortable:hover {
  background: darkgray;
}
.lxd-table thead th .fa {
  margin-left: 5px;
  font-size: 11px;
  opacity: 0.7;
}
.lxd-table tbody tr {
  border-bottom: 1px solid #f0f2f5;
  transition: background 0.1s;
}
.lxd-table tbody tr:last-child {
  border-bottom: none;
}
.lxd-table td {
  padding: 10px 14px;
  vertical-align: middle;
}
.lxd-row {
  cursor: default;
}
.lxd-row:hover {
  background: #f5f8ff;
}
.lxd-row:nth-child(even) {
  background: #fafbfc;
}
.lxd-row:nth-child(even):hover {
  background: #f0f4ff;
}

/* Name */
.lxd-name {
  font-weight: 600;
  color: #2c7be5;
}

/* Node */
.lxd-node {
  display: inline-block;
  background: #f0f2f5;
  color: #495057;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
}

/* Badges */
.lxd-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: capitalize;
}
.lxd-badge--running {
  background: #d4edda;
  color: #155724;
}
.lxd-badge--stopped {
  background: #fdeeed;
  color: #842029;
}
.lxd-badge--other {
  background: #fff3cd;
  color: #856404;
}

/* Mono / date */
.lxd-mono {
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 12px;
  color: #495057;
}
.lxd-date {
  font-size: 12px;
  color: #6c757d;
}

/* Empty state */
.lxd-empty {
  text-align: center;
  padding: 48px 0;
  color: #6c757d;
  font-size: 14px;
}

/* Skeleton loader */
.lxd-skeleton {
  height: 38px;
  background: linear-gradient(90deg, #f0f2f5 25%, #e4e6ea 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite;
}
@keyframes skeleton-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Pagination */
.lxd-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}
.lxd-pagination__info {
  font-size: 13px;
  color: #6c757d;
}
.lxd-pagination__controls {
  display: flex;
  gap: 4px;
}
.lxd-page-btn {
  min-width: 34px;
  height: 34px;
  padding: 0 8px;
  border: 1px solid #dee2e6;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lxd-page-btn:hover:not(:disabled) {
  background: #e9ecef;
  border-color: #adb5bd;
}
.lxd-page-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.lxd-page-btn--active {
  background: #2c7be5;
  border-color: #2c7be5;
  color: #fff;
  font-weight: 700;
}
.lxd-page-btn--active:hover {
  background: #1a68d1 !important;
}

/* Copy cell */
.lxd-copy-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}
.lxd-copy-cell--center {
  justify-content: center;
}
.lxd-copy-btn {
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: #adb5bd;
  font-size: 12px;
  line-height: 1;
  border-radius: 3px;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;
  flex-shrink: 0;
}
.lxd-row:hover .lxd-copy-btn {
  opacity: 1;
}
.lxd-copy-btn:hover {
  color: #2c7be5;
  background: #e8f0fe;
}

/* Profile tags */
.lxd-profiles {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
}
.lxd-profile-tag {
  display: inline-block;
  background: #e8f0fe;
  color: #1a56c4;
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 11px;
  font-weight: 600;
}

/* Type */
.lxd-type {
  display: inline-block;
  background: #f3f0ff;
  color: #6b21d6;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}
</style>
